import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { httpClientReactAdmin } from '../../lib';
import { DEFAULT_API_ADMIN } from '../../constants';

type CouponPlace = { id: string; name: string | null };

type CouponWithPlaces = {
  id: string;
  redeemed: boolean;
  refunded: boolean;
  expiresAt: string | null;
  barcode: string | null;
  integrationTicket: string | null;
  giftProductId: string | null;
  giftId: string | null;
  productId: string | null;
  placeId: string | null;
  integration: string | null;
  isRti: boolean;
  isEvo: boolean;
  isGift: boolean;
  redeemAnywhere?: boolean;
  places?: CouponPlace[];
};

const needsGeneration = (coupon: CouponWithPlaces) => {
  if (coupon.redeemed || coupon.refunded) return false;
  if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) return false;
  if (coupon.barcode || coupon.integrationTicket) return false;
  return !!(coupon.integration || coupon.isRti || coupon.isEvo || coupon.isGift || coupon.giftId || coupon.giftProductId);
};

const RedeemCouponAdminButton = () => {
  const record = useRecordContext<CouponWithPlaces>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [places, setPlaces] = useState<CouponPlace[]>([]);
  const [placeId, setPlaceId] = useState<string>('');

  if (!record || !needsGeneration(record)) return null;

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/coupons/${record.id}`,
        { method: 'GET' },
      );
      const coupon = json?.data || json;
      const fetchedPlaces: CouponPlace[] = coupon?.places || [];
      setPlaces(fetchedPlaces);
      // Auto-pré-seleciona se só houver 1 posto, ou usa o placeId de origem como fallback
      if (fetchedPlaces.length === 1) {
        setPlaceId(fetchedPlaces[0].id);
      } else if (fetchedPlaces.length === 0 && coupon?.placeId) {
        setPlaceId(coupon.placeId);
      } else {
        setPlaceId('');
      }
    } catch (err: any) {
      notify(err?.body?.message || 'Erro ao carregar locais do cupom', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setPlaces([]);
    setPlaceId('');
  };

  const submit = async () => {
    if (!placeId && (record.giftProductId || record.giftId)) {
      notify('Selecione o posto de resgate', { type: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const body = JSON.stringify(placeId ? { placeId } : {});
      const { json } = await httpClientReactAdmin(
        `${DEFAULT_API_ADMIN}/coupons/${record.id}`,
        { method: 'PATCH', body },
      );
      const data = json?.data || json;
      const code = data?.barcode || data?.integrationTicket;
      notify(code ? `Cupom gerado: ${code}` : 'Cupom resgatado com sucesso', { type: 'success' });
      close();
      refresh();
    } catch (err: any) {
      const message = err?.body?.message || err?.message || 'Erro ao gerar cupom';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const showPlaceSelect = places.length > 0;

  return (
    <>
      <Button onClick={(e) => { e.stopPropagation(); openDialog(); }} size="small" variant="outlined">
        Gerar cupom
      </Button>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>Gerar cupom (admin)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reproduz o fluxo do cliente: chama a integração externa (RTI/EVO/Trevo) ou gera código local
            conforme o posto selecionado.
          </DialogContentText>
          {loading && places.length === 0 ? (
            <Typography variant="body2">Carregando locais…</Typography>
          ) : showPlaceSelect ? (
            <FormControl fullWidth size="small">
              <InputLabel id="redeem-place-label">Posto de resgate</InputLabel>
              <Select
                labelId="redeem-place-label"
                label="Posto de resgate"
                value={placeId}
                onChange={(e) => setPlaceId(e.target.value as string)}
              >
                {places.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name || p.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2">
              Cupom sem locais configurados. Será resgatado no local de origem
              {record.placeId ? '.' : ' (não cadastrado).'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={loading}>Cancelar</Button>
          <Button onClick={submit} disabled={loading} variant="contained">Gerar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RedeemCouponAdminButton;
